import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  Buildings, Bank, Wallet, UsersThree, Files as FilesIcon, ChartLineUp,
  CheckCircle, X, Sparkle
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';

const STEPS = [
  {
    id: 'company',
    title: 'Set Company Details',
    desc: 'Add your company name, address, tax IDs, currency and logo. These appear on every invoice and PDF.',
    icon: Buildings,
    to: '/dashboard/company',
    cta: 'Open Company Settings',
    tint: 'from-blue-500/15 to-blue-500/5 text-blue-700 border-blue-500/20',
  },
  {
    id: 'cash',
    title: 'Add a Cash Account',
    desc: 'Add at least one cash account so payment vouchers work. Default "1000 — Cash" usually exists.',
    icon: Wallet,
    to: '/dashboard/accounts',
    cta: 'Open Chart of Accounts',
    tint: 'from-emerald-500/15 to-emerald-500/5 text-emerald-700 border-emerald-500/20',
  },
  {
    id: 'bank',
    title: 'Add a Bank Account',
    desc: 'Add your business bank accounts for non-cash payments and receipts.',
    icon: Bank,
    to: '/dashboard/accounts',
    cta: 'Add Bank Account',
    tint: 'from-violet-500/15 to-violet-500/5 text-violet-700 border-violet-500/20',
  },
  {
    id: 'contacts',
    title: 'Add Customers & Suppliers',
    desc: 'Add your first contacts so you can quickly raise invoices and record bills.',
    icon: UsersThree,
    to: '/dashboard/contacts',
    cta: 'Add Contacts',
    tint: 'from-amber-500/15 to-amber-500/5 text-amber-700 border-amber-500/20',
  },
  {
    id: 'first_doc',
    title: 'Create Your First Invoice',
    desc: 'Raise an invoice or quotation — see how the entire accounting pipeline kicks in automatically.',
    icon: FilesIcon,
    to: '/dashboard/sales',
    cta: 'Create Invoice',
    tint: 'from-rose-500/15 to-rose-500/5 text-rose-700 border-rose-500/20',
  },
  {
    id: 'reports',
    title: 'Explore Reports',
    desc: 'Trial Balance, P&L, Balance Sheet — all auto-generated from your transactions.',
    icon: ChartLineUp,
    to: '/dashboard/reports',
    cta: 'View Reports',
    tint: 'from-cyan-500/15 to-cyan-500/5 text-cyan-700 border-cyan-500/20',
  },
];

const STORAGE_KEY = 'erp_welcome_wizard_state_v1';

/**
 * First-login welcome wizard. Shows once per user (persisted in localStorage).
 * Skip and "Don't show again" both honored. Per-step completion is checkable
 * by the user; nothing is blocked.
 */
export default function WelcomeWizard() {
  const { user } = useAuth();
  const { company } = useCompany();
  const [open, setOpen] = useState(false);
  const [completed, setCompleted] = useState({});

  // Decide whether to open on first render
  useEffect(() => {
    if (!user?.id) return;
    const raw = localStorage.getItem(STORAGE_KEY);
    let state;
    try { state = raw ? JSON.parse(raw) : {}; } catch { state = {}; }
    const userKey = `${user.id}`;
    const userState = state[userKey] || {};
    if (userState.dismissed) return;
    setCompleted(userState.completed || {});
    // Only open for users created within the last 24 hours OR who have never seen it
    if (!userState.shown) {
      setOpen(true);
    }
  }, [user?.id]);

  const persist = (next) => {
    if (!user?.id) return;
    const raw = localStorage.getItem(STORAGE_KEY);
    let state;
    try { state = raw ? JSON.parse(raw) : {}; } catch { state = {}; }
    state[user.id] = { ...(state[user.id] || {}), ...next };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  };

  const markShown = () => persist({ shown: true });
  const handleSkip = () => { persist({ shown: true, dismissed: true }); setOpen(false); };
  const handleClose = () => { markShown(); setOpen(false); };
  const toggleStep = (id) => {
    const next = { ...completed, [id]: !completed[id] };
    setCompleted(next);
    persist({ completed: next, shown: true });
  };

  if (!user?.id) return null;
  const totalDone = Object.values(completed).filter(Boolean).length;
  const progress = Math.round((totalDone / STEPS.length) * 100);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="welcome-wizard">
        <div className="p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-medium px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 mb-2">
                <Sparkle size={14} weight="fill" /> Welcome to BizCore
              </div>
              <h2 className="text-2xl font-heading font-semibold tracking-tight">
                Hi {user?.first_name || 'there'} 👋
              </h2>
              <p className="text-muted-foreground text-sm mt-1">
                You're all set with <span className="font-medium">{company?.name || 'your company'}</span>.
                Here's a quick 5-minute setup to get the most out of the app.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose} className="text-muted-foreground" data-testid="welcome-close">
              <X size={18} />
            </Button>
          </div>

          {/* Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{totalDone} of {STEPS.length} steps complete</span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          {/* Step cards */}
          <div className="grid sm:grid-cols-2 gap-3">
            <AnimatePresence>
              {STEPS.map((s, i) => {
                const done = !!completed[s.id];
                return (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * i }}
                  >
                    <Card className={`bg-gradient-to-br ${s.tint} border h-full ${done ? 'opacity-60' : ''}`}>
                      <CardContent className="p-4 flex flex-col gap-3 h-full">
                        <div className="flex items-start justify-between">
                          <s.icon size={22} weight="duotone" />
                          <button
                            onClick={() => toggleStep(s.id)}
                            className="text-xs flex items-center gap-1 hover:underline"
                            data-testid={`welcome-step-${s.id}-toggle`}
                          >
                            <CheckCircle size={16} weight={done ? 'fill' : 'regular'} />
                            {done ? 'Done' : 'Mark done'}
                          </button>
                        </div>
                        <div>
                          <div className={`font-semibold text-sm ${done ? 'line-through' : ''}`}>{s.title}</div>
                          <div className="text-xs opacity-75 mt-1 leading-snug">{s.desc}</div>
                        </div>
                        <Link to={s.to} onClick={handleClose} className="mt-auto" data-testid={`welcome-step-${s.id}-link`}>
                          <Button variant="outline" size="sm" className="w-full">{s.cta}</Button>
                        </Link>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={handleSkip} data-testid="welcome-skip">
              Don't show this again
            </Button>
            <div className="text-xs text-muted-foreground">
              You can re-open this anytime via the <strong>?</strong> menu in the topbar.
            </div>
            <Button onClick={handleClose} data-testid="welcome-continue">Continue to Dashboard</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper for topbar — re-open on demand
export function reopenWelcomeWizard(userId) {
  if (!userId) return;
  const raw = localStorage.getItem(STORAGE_KEY);
  let state;
  try { state = raw ? JSON.parse(raw) : {}; } catch { state = {}; }
  state[userId] = { ...(state[userId] || {}), shown: false, dismissed: false };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.location.reload();
}
